// Photos on the dock form (migration 019). ALWAYS OPTIONAL (Amit, 22 Oct 2026):
// nothing here is ever required, and nothing waits for it.
//
// Two ways in, both 56px targets: the camera (capture="environment" opens the
// back camera on a phone or tablet) and the gallery (the same input without
// capture). Every photo is scaled to a JPEG on the device before it is kept
// (photoScale.ts), so what waits in the offline queue is ~0.5 MB, not 8.
import { useEffect, useMemo, useRef, useState } from 'react';
import { opFocus } from '../../shells/OperatorShell';
import { PhotoUnreadable, scalePhoto } from './photoScale';
import { MAX_PHOTOS, type DraftPhoto } from './receiveModel';

// disabled:opacity-50 (UI/UX audit, 31 Oct 2026, UX-53): at the 10-photo cap (or while a photo is
// being prepared) Take and Choose are disabled but looked exactly as live, so a press that did
// nothing read as a broken button. Same dimming as every other disabled operator button.
const BTN =
  `flex min-h-[56px] cursor-pointer items-center justify-center rounded-(--radius) border border-(--border-strong) ` +
  `bg-(--card) px-(--s3) text-(length:--fs-sm) font-semibold disabled:cursor-default disabled:opacity-50 ${opFocus}`;

/** An object URL per photo, revoked when the photo or the component goes. */
function useThumbs(photos: readonly DraftPhoto[]) {
  const urls = useMemo(() => new Map(photos.map((p) => [p.key, URL.createObjectURL(p.blob)])), [photos]);
  useEffect(() => () => urls.forEach((u) => URL.revokeObjectURL(u)), [urls]);
  return urls;
}

let seq = 0;

export function PhotoPicker({
  label,
  photos,
  total,
  onAdd,
  onRemove,
  max = MAX_PHOTOS,
  per = 'inspection',
}: {
  /** What the photos are of, for the buttons' accessible names ("the delivery", "row 2"). */
  label: string;
  /** This target's photos. */
  photos: readonly DraftPhoto[];
  /** Every photo on the form: the cap is per inspection. */
  total: number;
  onAdd: (photos: Array<Pick<DraftPhoto, 'key' | 'blob' | 'capturedAtDevice'>>) => void;
  onRemove: (key: string) => void;
  /** The cap and what it is per: the dock's 10 per inspection by default; the line's 4 per reading. */
  max?: number;
  per?: string;
}) {
  const camera = useRef<HTMLInputElement>(null);
  const gallery = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const thumbs = useThumbs(photos);
  const room = max - total;

  async function take(files: FileList | null) {
    if (!files || files.length === 0) return;
    setBusy(true);
    setProblem(null);
    const added: Array<Pick<DraftPhoto, 'key' | 'blob' | 'capturedAtDevice'>> = [];
    const problems: string[] = [];
    for (const file of [...files].slice(0, Math.max(0, room))) {
      try {
        added.push({ key: `photo-${++seq}`, blob: await scalePhoto(file), capturedAtDevice: new Date().toISOString() });
      } catch (err) {
        problems.push(err instanceof PhotoUnreadable ? err.message : 'This photo could not be prepared on this device.');
      }
    }
    if (files.length > room) problems.push(`Only ${max} photos can go with one ${per}.`);
    if (added.length > 0) onAdd(added);
    setProblem(problems.length > 0 ? [...new Set(problems)].join(' ') : null);
    setBusy(false);
    if (camera.current) camera.current.value = '';
    if (gallery.current) gallery.current.value = '';
  }

  return (
    <div className="flex flex-col gap-(--s2)">
      {photos.length > 0 ? (
        <ul className="m-0 flex list-none flex-wrap gap-(--s2) p-0" aria-label={`Photos of ${label}`}>
          {photos.map((p, i) => (
            <li key={p.key} className="flex flex-col items-center gap-(--s1)">
              <img src={thumbs.get(p.key)} alt={`Photo ${i + 1} of ${label}`} className="h-[88px] w-[88px] rounded-(--radius) border border-(--border) object-cover" />
              <button type="button" className={`${BTN} w-[88px] px-0`} onClick={() => onRemove(p.key)} aria-label={`Remove photo ${i + 1} of ${label}`}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="grid grid-cols-2 gap-(--s2) sm:flex sm:flex-wrap">
        <button type="button" className={BTN} disabled={busy || room <= 0} onClick={() => camera.current?.click()} aria-label={`Take a photo of ${label}`}>
          {busy ? 'Preparing…' : 'Take photo'}
        </button>
        <button type="button" className={BTN} disabled={busy || room <= 0} onClick={() => gallery.current?.click()} aria-label={`Choose photos of ${label}`}>
          Choose photo
        </button>
        <input ref={camera} type="file" accept="image/*" capture="environment" multiple hidden onChange={(e) => void take(e.target.files)} />
        <input ref={gallery} type="file" accept="image/*" multiple hidden onChange={(e) => void take(e.target.files)} />
      </div>
      {room <= 0 ? (
        <p className="m-0 text-(length:--fs-sm) text-(--muted)">{max} photos is the most one {per} keeps.</p>
      ) : null}
      {problem ? (
        <p role="alert" className="m-0 text-(length:--fs-sm) font-semibold text-(--warn)">
          {problem}
        </p>
      ) : null}
    </div>
  );
}
